import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BaPerson } from '../core-data-model/BehavioralAssessmentPerson.model';
import { Person } from '../core-data-model/Person.model';
import { notificationOnError } from '../core-services/Notifications.service';
import { PersonDashboardService } from '../core-services/PersonDashboard.service';
import { Logger } from '../system-services/Logger/Logger.service';
import { TranslationService } from '../translations/Translation.service';

@Component({
    selector: 'pattern-insights',
    template: `
        @if (isLoading && usePILoadingAnimation) {
            <tapestry-spinner></tapestry-spinner>
        } @else {
            <div class="insights-sub-section">
                <p class="title-md-16">{{ 'stengthsheader' | translate }}:</p>
                @for (strength of strengths; track strength) {
                    <div class="insights-sub-section-content">
                        {{ strength }}
                    </div>
                }
            </div>
            @if (TR.isEnglish()) {
                <div class="insights-sub-section">
                    <p class="title-md-16">
                        {{ 'design.preferredworkstyle' | translate }}:
                    </p>
                    @for (style of workStyle; track style) {
                        <div
                            class="insights-sub-section-content"
                            [innerHTML]="style"
                        ></div>
                    }
                </div>
            }
            <div class="insights-sub-section">
                <p class="title-md-16">
                    {{ 'design.potentialblindspots' | translate }}:
                </p>
                @for (blindSpot of blindSpots; track blindSpot) {
                    <div>
                        <div
                            class="insights-sub-section-content"
                            [innerHTML]="blindSpot"
                        ></div>
                    </div>
                }
            </div>
        }
    `,
    styleUrls: ['./pattern-insights.less'],
    standalone: false
})
export class PatternInsightsComponent implements OnInit {
    // Only one of Person or BaPerson should be provided
    // This component should be updated to accept only one type in the future
    // but to avoid breaking changes, both are kept as part of the implementation
    // of common/person endpoint in GlobalSearchPersonSnapshotComponent.
    //
    // Ultimatley a BaPerson is needed to display the insights, so if
    // other parent components can provide this input it will avoid
    // unnecessary API calls.
    @Input()
    person: Person;

    @Input()
    baPerson: BaPerson;

    @Input()
    usePILoadingAnimation = true;

    blindSpots: string[] = [];
    workStyle: string[] = [];
    strengths: string[] = [];

    isLoading = false;

    baPersonData: BaPerson;

    @Output()
    loading: EventEmitter<boolean> = new EventEmitter();

    constructor(
        public TR: TranslationService,
        private readonly logger: Logger,
        private readonly personDashboard: PersonDashboardService
    ) {}

    async ngOnInit() {
        await this.setBaPerson();

        this.filterStrongestBehaviorsText();
        this.strengths = this.getPersonStrengths(this.baPersonData);
        this.blindSpots = this.getBlindSpotText(this.baPersonData);
    }

    private async setBaPerson() {
        if (!this.person && !this.baPerson) {
            this.logger.logError('No person or baPerson provided', {});
        }

        if (this.baPerson) {
            this.baPersonData = this.baPerson;
        } else if (
            this.person &&
            (this.person.hasBehavioralPattern() || this.person.referencePattern)
        ) {
            this.baPersonData = await this.getBaPersonData(this.person.id);
        }
    }

    @notificationOnError()
    private async getBaPersonData(personId: string): Promise<BaPerson> {
        try {
            this.isLoading = true;
            this.loading.emit(this.isLoading);
            return await this.personDashboard.getBaPersonDetails(personId);
        } catch (error) {
            this.logger.logError('Retrieve person snapshot failed', {
                error: error
            });
        } finally {
            this.isLoading = false;
            this.loading.emit(this.isLoading);
        }
    }

    private filterStrongestBehaviorsText() {
        // We only need the first sentence('.')  of the first two <li> contents of the first block and
        // first sentence('.') of the second block's <li> content.
        // Simply put, the first sentence of 1,2,4 bullets from the BA report
        const firstTextBlockLimiter = 2;
        const secondTextBlockLimiter = 1;
        this.workStyle = [];
        this.getListItems(
            this.baPersonData.preferredWorkStyleKeys[0],
            this.workStyle,
            firstTextBlockLimiter
        );
        this.getListItems(
            this.baPersonData.preferredWorkStyleKeys[1],
            this.workStyle,
            secondTextBlockLimiter
        );
    }

    private getPersonStrengths(person: BaPerson) {
        return this.TR.key(person.strengthsKey).trim().split('|');
    }

    private getBlindSpotText(person: BaPerson) {
        return this.TR.key(person.cautionAreasKey).trim().split('|');
    }

    private getListItems(
        textBlock: string,
        collection: string[],
        limiter: number
    ) {
        const translatedText = this.TR.key(textBlock);
        const items = translatedText.split('</li>').filter((item) => item);

        // Ensure there are enough items in the array
        for (let i = 0; i < limiter && i < items.length; i++) {
            if (items[i]) {
                // Replaces first name placeholder when present
                items[i] = items[i].replace('[fname]', '').replace('<li>', '');
                const workStyleItems = items[i].trim().split('.');

                if (workStyleItems.length > 0) {
                    collection.push(workStyleItems[0]); // Only add the first sentence
                }
            }
        }
    }
}
